import React, { useMemo, useCallback, ReactNode } from 'react';
import StockStatus from '@prisjakt/ui-stock-status';
import Rating from '@prisjakt/ui-rating';
import { CountTranslation } from '@prisjakt/ui-rating/src/types';
import Lazy from '../../../../../components/lazy';
import ShippingLabel from '../../../../../components/shipping-label';
import { PropertyValue } from '../../../../../components/property-value';
import ActiveCampaignBadge from '../../active-campaign-badge';
import * as S from './styles-row';
import { SortOptionEnum } from '../../../../product/components/sort-options/types';
import { Product, ProductCollectionPropertyColumn } from '../../../../../types';
import { ProductComparisonProps } from '../types';

const NO_CAMPAIGNS: never[] = [];
const ImagePlaceholder = <S.ImagePlaceholder />;
const RatingTranslationKey = {
  domain: 'product',
  key: 'product-card.n-ratings',
} as CountTranslation;

type Props = {
  product: Product;
  propertyColumns: ProductCollectionPropertyColumn[];
  isIndicatingNewColumn: boolean;
  productCampaigns?: number[];
  highlightedName?: ReactNode;
} & ProductComparisonProps;

export const ProductRow = ({
  product,
  propertyColumns,
  isSelectingProducts,
  selectedProducts,
  selectProductById,
  deselectProductById,
  isIndicatingNewColumn,
  productCampaigns = NO_CAMPAIGNS,
  highlightedName,
}: Props) => {
  const isSelected = selectedProducts.some(p => p.id === product.id);
  const onSelect = useCallback(
    () => selectProductById(product.id, 'productCard'),
    [product.id, selectProductById]
  );
  const onDeselect = useCallback(
    () => deselectProductById(product.id, 'productCard'),
    [product.id, deselectProductById]
  );
  const onToggleSelected = isSelected ? onDeselect : onSelect;
  const linkProps = useMemo(() => {
    const baseLinkProps = {
      ariaLabel: product.name,
      dataTest: 'ProductListItem',
    };

    return isSelectingProducts
      ? { ...baseLinkProps, onClick: onToggleSelected }
      : { ...baseLinkProps, to: product.pathName };
  }, [product, isSelectingProducts, onToggleSelected]);

  return (
    <S.Tr selected={isSelected}>
      <S.NameTd>
        <S.NameWrapper>
          {isSelectingProducts && (
            <S.CompareCheckbox
              selected={isSelected}
              onSelect={onSelect}
              onDeselect={onDeselect}
              aria-label={product.name}
            />
          )}
          <S.NameLink {...linkProps}>
            <Lazy offset={0} placeholder={ImagePlaceholder}>
              <S.Image
                name={product.name}
                src={(product.media && product.media.first) || ''}
                fallback={product.category?.logo || undefined}
              />
            </Lazy>
            <S.ProductNameTable>
              {highlightedName || product.name}
            </S.ProductNameTable>
            <S.CampaignBadgeWrapper>
              <ActiveCampaignBadge includedCampaigns={productCampaigns} />
            </S.CampaignBadgeWrapper>
          </S.NameLink>
        </S.NameWrapper>
      </S.NameTd>
      <S.Td {...linkProps}>
        {product.priceSummary.regular && (
          <S.Price value={product.priceSummary.regular || ''} />
        )}
        <br />
        <ShippingLabel
          toneDown
          exclShipping={product.priceSummary.regular || 0}
          inclShipping={product.priceSummary.includeShipping}
          currentSort={SortOptionEnum.PRICE}
          compact
        />
      </S.Td>
      <S.Td {...linkProps}>
        <StockStatus status={product.stockStatus || undefined} />
      </S.Td>
      <S.Td {...linkProps}>
        <Rating
          rate={product.userReviewSummary.rating}
          singleStar
          showRate
          showCounter
          count={product.userReviewSummary.count}
          countTranslation={RatingTranslationKey}
        />
      </S.Td>
      {propertyColumns.map((column, index) => {
        const isLastColumn = index === propertyColumns.length - 1;
        return (
          <S.PropertyTd
            key={column.id}
            indicateNewColumn={isIndicatingNewColumn && isLastColumn}
            {...linkProps}
          >
            <ProductPropertyValue product={product} column={column} />
          </S.PropertyTd>
        );
      })}
    </S.Tr>
  );
};

type ProductPropertyValueProps = {
  product: Product;
  column: ProductCollectionPropertyColumn;
};

const ProductPropertyValue = ({
  product,
  column,
}: ProductPropertyValueProps) => {
  // Some property columns are not actual properties, but we still treat them as such for the
  // most part. This handles how to render the values for those on a case-by-case basis.
  if (column.__typename === 'ProductCollectionSimulatedPropertyColumn') {
    switch (column.id) {
      case 'brand':
        return <>{product.brand?.name}</>;
      default:
        return null;
    }
  }

  const property = getProductProperty(product, column.id);
  if (!property) {
    return null;
  }

  return <PropertyValue property={property} />;
};

function getProductProperty(product: Product, propertyId: string) {
  const productProperties =
    product.properties?.nodes && Array.isArray(product.properties?.nodes)
      ? product.properties.nodes
      : [];
  return productProperties.find(p => p.id === propertyId);
}
